/**
 * Heap - Analytics tracking component
 *
 * Loads the Heap analytics script when the dedicated `VITE_HEAP_APP_ID`
 * config flag is set. The flag is intentionally unset in every environment,
 * so this loader is dormant until explicitly enabled.
 * This component renders nothing visually - it only loads the tracking script.
 *
 * Usage:
 * Mounted ONCE at the app root, inside `<AnalyticsProvider>` in `RoutedApp`
 * — not per auth layout — so a single loader covers
 * pre-auth/marketing/login surfaces too.
 *
 * Privacy: the loader is configured autocapture-OFF by default (see
 * HEAP_LOAD_CONFIG below) so that whenever activation happens (an app id is set)
 * Heap never silently captures the addresses, prices, EMD, or contract filenames
 * that fill these pages. Custom events still flow through the gated transport in
 * `common/analytics/heap.ts`.
 */

import { useEffect } from 'react'
import { config } from '@/config'
import {
  scrubHeapMessage,
  URL_SCRUB_TRANSFORMER_NAME,
  URL_SCRUB_PIPELINES,
} from '@/common/analytics/urlScrub'

// Extend window type for Heap
interface HeapAnalytics {
  load: (envId: string, config?: Record<string, unknown>) => void
  /** Register a message transformer fn on a pipeline ('general' | 'metadata'). */
  addTransformerFn?: (name: string, fn: (message: unknown) => unknown, pipeline?: string) => void
  envId?: string
  clientConfig?: Record<string, unknown>
  [key: string]: unknown
}

declare global {
  interface Window {
    heap?: HeapAnalytics
    heapReadyCb?: Array<{ name: string; fn: () => void }>
  }
}

/**
 * Privacy-hardened autocapture config passed to `heap.load()`. This MUST be in
 * place now (even while the loader is dormant) so activation is privacy-safe by
 * default — these pages are full of addresses, prices, EMD, and contract
 * filenames that we never want silently captured.
 *
 * Option names verified against Heap's web SDK docs
 * (developers.heap.io/docs/web, .../ignoring-sensitive-data-and-pii):
 *  - `disableTextCapture: true` — stops autocapture of the text content of DOM
 *    elements (the primary leak surface: rendered addresses/prices/EMD text).
 *  - `disableInteractionEvents: ['change', 'submit']` — stops autocapture of
 *    `change`/`submit`, where typed/selected INPUT VALUES (addresses, prices,
 *    EMD amounts) would otherwise be captured. `click` autocapture is left ON
 *    for button/link click analytics: Heap records a click by DOM selector
 *    (e.g. `button.foo`), never the element's value, and `disableTextCapture`
 *    keeps its text out — so no PII rides on a click.
 *  - `disablePageviewAutocapture: true` — stops navigation-triggered pageview
 *    autocapture so filter/sort/pagination URL churn cannot burst pageviews. We
 *    fire native pageviews MANUALLY (common/analytics, route TEMPLATE only —
 *    never a resolved URL) for SPA navigations instead. Heap still fires ONE
 *    unavoidable autocapture pageview on hard load / session-restart (this flag
 *    does not cover it); we pin Heap to the route template via `setPath` so even
 *    that one reports the template, not the live URL.
 *  - `disableSessionReplay: true` — defense-in-depth: never record a DOM/session
 *    replay of these PII-dense pages. (The replay plugin is not loaded today, so
 *    this is currently inert, but we set it explicitly so replay can never turn
 *    on silently via a future SDK/config change.)
 *
 * URL query-string / path-id capture is handled SEPARATELY by a transformer:
 * `disablePageviewAutocapture` does NOT cover the initial page-load /
 * session-expiration pageview, which Heap still records with the resolved
 * `window.location` (path incl. ids + query string). Heap exposes no `load()`
 * key to suppress that, so we register `scrubHeapMessage` via `addTransformerFn`
 * below to blank `query`/`hash` and redact id-bearing path segments before any
 * event leaves the browser. See `common/analytics/urlScrub.ts`.
 */
const HEAP_LOAD_CONFIG: Record<string, unknown> = {
  disableTextCapture: true,
  disableInteractionEvents: ['change', 'submit'],
  disablePageviewAutocapture: true,
  disableSessionReplay: true,
}

export function Heap() {
  // Gate on the dedicated Heap config flag. When unset (the default in every
  // environment) the loader is dormant by intent and never runs.
  const heapAppId = config.heapAppId

  useEffect(() => {
    if (!heapAppId) {
      return
    }

    // Don't load if already initialized
    if (window.heap && typeof window.heap.load === 'function' && window.heap.envId) {
      return
    }

    // Initialize Heap ready callback queue
    window.heapReadyCb = window.heapReadyCb || []

    // Define stub methods
    const methodNames = [
      'init',
      'startTracking',
      'stopTracking',
      'track',
      'resetIdentity',
      'identify',
      'getSessionId',
      'getUserId',
      'getIdentity',
      'addUserProperties',
      'addEventProperties',
      'removeEventProperty',
      'clearEventProperties',
      'addAccountProperties',
      'addAdapter',
      'addTransformer',
      'addTransformerFn',
      'onReady',
      'addPageviewProperties',
      'removePageviewProperty',
      'clearPageviewProperties',
      'trackPageview',
      'setPath',
    ]

    const createStub = (methodName: string) => {
      return (...args: unknown[]) => {
        window.heapReadyCb!.push({
          name: methodName,
          fn: () => {
            const heapMethod = window.heap?.[methodName]
            if (typeof heapMethod === 'function') {
              heapMethod.apply(window.heap, args)
            }
          },
        })
      }
    }

    // Build heap object with stubs
    const heapObj: HeapAnalytics = {
      load: (envId: string, clientConfig?: Record<string, unknown>) => {
        heapObj.envId = envId
        heapObj.clientConfig = clientConfig || {}
        heapObj.clientConfig.shouldFetchServerConfig = false

        // Create and insert script into head
        const script = document.createElement('script')
        script.type = 'text/javascript'
        script.async = true
        script.src = `https://cdn.us.heap-api.com/config/${envId}/heap_config.js`

        // Insert before the first <script> if one exists; otherwise fall back
        // to appending to <head>. The original `firstScript.parentNode` form
        // threw when the document had no <script> tag (firstScript undefined).
        const firstScript = document.getElementsByTagName('script')[0]
        if (firstScript?.parentNode) {
          firstScript.parentNode.insertBefore(script, firstScript)
        } else {
          document.head.appendChild(script)
        }
      },
    }

    // Add stub methods
    for (const methodName of methodNames) {
      heapObj[methodName] = createStub(methodName)
    }

    // Assign to window and load with the autocapture-off privacy config.
    // `shouldFetchServerConfig: false` is preserved inside load() above; the
    // remaining keys disable sensitive autocapture surfaces.
    window.heap = heapObj
    window.heap.load(heapAppId, HEAP_LOAD_CONFIG)

    // Register the URL query-string scrub transformer IMMEDIATELY after load, via
    // the stub queue, so it is in place before the SDK flushes its initial
    // pageview (the one `disablePageviewAutocapture` does not cover, which would
    // otherwise carry the resolved query string). Filters `query` to an allow-list
    // of marketing/attribution params (utm_*, gclid, ref, …) and blanks `hash` on
    // every Heap url object; the resolved PATH is kept (opaque entity ids).
    //
    // Registered on BOTH pipelines: 'metadata' carries the autocaptured pageview/
    // session messages (the standalone "View <path>" events + initial_pageview_info
    // — the initial-pageview leak vector); 'general' carries the url snapshot on
    // our custom track events. See urlScrub.ts.
    for (const pipeline of URL_SCRUB_PIPELINES) {
      window.heap.addTransformerFn?.(`${URL_SCRUB_TRANSFORMER_NAME}-${pipeline}`, scrubHeapMessage, pipeline)
    }
  }, [heapAppId])

  // This component renders nothing
  return null
}
